import type { Route, RouteWithChildren } from './types'
import type { Location } from '@remix-run/router'
import type { NavLinkProps } from 'react-router-dom'

import { CaretDownOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import cx from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import './menu-item.css'
import { useMenu } from '../../../components/Menu/context'

const isActiveLocation = (l: Location, to?: string, matchPaths?: string[]): boolean => {
  const currentPath = l.pathname

  if (to === currentPath) {
    return true
  }

  return Array.isArray(matchPaths) && matchPaths.includes(currentPath)
}

export function MenuItem({ title, matchPaths, ...rest }: Route): JSX.Element {
  const location: Location = useLocation()

  const isActive = useMemo(
    () => isActiveLocation(location, rest.to, matchPaths),
    [location, rest.to, matchPaths]
  )

  if ('children' in rest) {
    return <RenderChildren route={{ ...rest, title }} />
  }
  return <Link {...rest} title={title} isActive={isActive} />
}

export function RenderChildren({ route }: { route: RouteWithChildren }): JSX.Element {
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const { open: menuOpen, setOpen: setMenuOpen } = useMenu()

  function handleChange(): void {
    setMenuOpen(true)
    setIsOpen(prev => !prev)
  }

  useEffect(() => {
    if (!menuOpen) {
      setIsOpen(false)
    }
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) {
      return
    }
    const hasActiveChild = route.children.some(c => isActiveLocation(location, c.to, c.matchPaths))
    if (hasActiveChild) {
      setIsOpen(true)
    }
  }, [location, route.children, menuOpen])

  const hasActiveLocation = useMemo(() => {
    return route.children.some(c => isActiveLocation(location, c.to, c.matchPaths))
  }, [location, route.children])

  return (
    <div className='flex flex-col'>
      <button
        onClick={handleChange}
        className='new-menu-item self-start hover:bg-zinc-100 rounded px-2 py-1.5 w-full'
      >
        <div className='flex flex-row justify-between text-zinc-700 items-center'>
          <div
            className={cx(
              'flex flex-row gap-3 items-center font-normal',
              !menuOpen && hasActiveLocation ? 'text-sky-600' : 'text-zinc-700'
            )}
          >
            <Tooltip title={route.title} placement='right'>
              {route.icon}
            </Tooltip>
            <span className='text-sm transition-all overflow-hidden whitespace-nowrap'>
              {route.title}
            </span>
          </div>
          <CaretDownOutlined
            className={cx('text-xs transition-transform duration-200', isOpen && 'rotate-180')}
          />
        </div>
      </button>

      <div
        className={cx(
          'relative gap-1 flex flex-col overflow-hidden transition-all ease-in-out pl-7',
          [isOpen ? 'max-h-[400px] mt-2' : 'max-h-[0px]']
        )}
      >
        {route.children.map(c => (
          <Link
            key={c.to}
            {...c}
            className='overflow-hidden whitespace-nowrap'
            title={c.title}
            isActive={isActiveLocation(location, c.to, c.matchPaths)}
          />
        ))}
        <div className='absolute left-3.5 h-full w-[1px] bg-zinc-200' />
      </div>
    </div>
  )
}

type LinkProps = NavLinkProps & { isActive: boolean; icon?: JSX.Element }

export function Link({ title, ...rest }: LinkProps): JSX.Element {
  return (
    <NavLink {...rest} end>
      <div
        className={cx(
          'flex flex-row gap-3 items-center text-sm font-normal px-2 py-1.5 whitespace-nowrap transition-colors rounded',
          rest.isActive ? 'bg-[#E9F4F8] text-[#005F7F]' : 'text-zinc-700 hover:bg-zinc-100'
        )}
      >
        <Tooltip title={title} placement='right'>
          {rest.icon}
        </Tooltip>
        {title}
      </div>
    </NavLink>
  )
}
